import {
  formatearFechaHora,
  NOMBRE_TIPO_CORRECCION,
  TIPOS_DE_CORRECCION,
  type TipoDeCorreccion,
} from "@gestiondoc/shared";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { conAviso, useTRPC } from "../api.ts";
import type { Marca, Marcas } from "../tipos.ts";
import { Boton, CLASE_ENTRADA, Pestanas, Pildora } from "./ui.tsx";

/**
 * Fase 4 · el panel "Correcciones" de la pantalla 09: las marcas de la revisión con su filtro, el
 * comentario general y, por marca, resolver, responder o quitar. `ItemDeMarca` también lo usa el
 * editor del autor para ver las marcas junto a su texto.
 */

export type FiltroDeMarcas = "todas" | "abiertas" | "resueltas";

/** El color de cada tipo de marca, el mismo que pinta la hoja. */
export const COLOR_TIPO: Record<TipoDeCorreccion, string> = {
  corregir: "bg-[#b42318]",
  sugerencia: "bg-[#b7791f]",
  comentario: "bg-[#1d4ed8]",
};

export function PanelDeMarcas({
  revisionId,
  datos,
  visibles,
  filtro,
  onFiltro,
  activa,
  onElegir,
  onAviso,
}: {
  revisionId: string;
  datos: Marcas;
  visibles: Marca[];
  filtro: FiltroDeMarcas;
  onFiltro: (f: FiltroDeMarcas) => void;
  activa: number | null;
  onElegir: (n: number) => void;
  onAviso: (m: string | null) => void;
}) {
  const [general, setGeneral] = useState(false);
  const abiertas = datos.marcas.filter((x) => x.estado === "abierta").length;
  const filtros: { clave: FiltroDeMarcas; nombre: string }[] = [
    { clave: "todas", nombre: "Todas" },
    { clave: "abiertas", nombre: `Abiertas (${abiertas})` },
    { clave: "resueltas", nombre: "Resueltas" },
  ];
  return (
    <div className="rounded-tarjeta border border-borde bg-white">
      <div className="px-4 pt-3">
        <h2 className="font-display text-[15px] font-bold text-marino">Correcciones</h2>
        <Pestanas
          opciones={filtros}
          activa={filtro}
          onCambio={onFiltro}
          className="mt-2 -mx-4 px-4"
        />
      </div>

      {datos.puedeMarcar && (
        <div className="border-b border-borde px-4 py-3">
          {general ? (
            <ComentarioGeneral
              revisionId={revisionId}
              onListo={() => setGeneral(false)}
              onAviso={onAviso}
            />
          ) : (
            <button
              type="button"
              onClick={() => setGeneral(true)}
              className="text-[12.5px] font-semibold text-marino hover:underline"
            >
              + Comentario general
            </button>
          )}
          {datos.seAncla && !general && (
            <p className="mt-1 text-[12px] text-texto-3">
              Para marcar una frase, selecciónala en el documento: el globo sale junto a ella.
            </p>
          )}
        </div>
      )}

      {visibles.length === 0 ? (
        <p className="px-4 py-6 text-center text-[12.5px] text-texto-3">
          {datos.marcas.length === 0 ? "Todavía no hay marcas." : "Ninguna marca en este filtro."}
        </p>
      ) : (
        <ol className="divide-y divide-borde/60">
          {visibles.map((x) => (
            <ItemDeMarca
              key={x.id}
              marca={x}
              activa={x.numero === activa}
              onElegir={() => onElegir(x.numero)}
              onAviso={onAviso}
            />
          ))}
        </ol>
      )}

      <p className="border-t border-borde bg-fondo px-4 py-3 text-[11.5px] leading-snug text-texto-3">
        Al devolver, quien elaboró recibe estas marcas ligadas al texto y las ve resaltadas en su
        documento. Cuando las resuelva y la reenvíe, la misma revisión vuelve a revisión con el
        historial intacto.
      </p>
    </div>
  );
}

/** Un comentario que no es de una frase (o de un documento que es un archivo). */
function ComentarioGeneral({
  revisionId,
  onListo,
  onAviso,
}: {
  revisionId: string;
  onListo: () => void;
  onAviso: (m: string | null) => void;
}) {
  const trpc = useTRPC();
  const [tipo, setTipo] = useState<TipoDeCorreccion>("comentario");
  const [comentario, setComentario] = useState("");
  const crear = useMutation(
    trpc.correcciones.crear.mutationOptions(
      conAviso(onAviso, () => {
        setComentario("");
        onListo();
      }),
    ),
  );
  function enviar(e: FormEvent) {
    e.preventDefault();
    crear.mutate({ revisionId, tipo, comentario });
  }
  return (
    <form
      onSubmit={enviar}
      className="space-y-2 border-b border-borde bg-marino-claro/40 px-4 py-3"
    >
      <div className="text-[11.5px] font-semibold tracking-[.06em] text-texto-3 uppercase">
        Comentario general
      </div>
      <div className="flex gap-1">
        {TIPOS_DE_CORRECCION.map((t) => (
          <button
            key={t}
            type="button"
            aria-pressed={tipo === t}
            onClick={() => setTipo(t)}
            className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12px] font-semibold ${
              tipo === t ? "border-marino bg-white text-marino" : "border-borde text-texto-3"
            }`}
          >
            <span className={`h-2 w-2 rounded-sm ${COLOR_TIPO[t]}`} />
            {NOMBRE_TIPO_CORRECCION[t]}
          </button>
        ))}
      </div>
      <textarea
        // biome-ignore lint/a11y/noAutofocus: se abre al pedirlo, para escribir enseguida
        autoFocus
        value={comentario}
        onChange={(e) => setComentario(e.target.value)}
        rows={3}
        placeholder="Qué hay que cambiar, o la pregunta"
        aria-label="Comentario general"
        className={CLASE_ENTRADA}
      />
      <div className="flex justify-end gap-2">
        <Boton variante="secundario" onClick={onListo} disabled={crear.isPending}>
          Cancelar
        </Boton>
        <Boton type="submit" disabled={crear.isPending || !comentario.trim()}>
          {crear.isPending ? "Guardando…" : "Guardar comentario"}
        </Boton>
      </div>
    </form>
  );
}

/** Una marca en el panel: quién, qué frase, el comentario, sus respuestas y sus acciones. */
export function ItemDeMarca({
  marca: x,
  activa,
  onElegir,
  onAviso,
}: {
  marca: Marca;
  activa: boolean;
  onElegir: () => void;
  onAviso: (m: string | null) => void;
}) {
  const trpc = useTRPC();
  const fila = useRef<HTMLLIElement>(null);
  const [respondiendo, setRespondiendo] = useState(false);
  const [respuesta, setRespuesta] = useState("");
  const resolver = useMutation(trpc.correcciones.resolver.mutationOptions(conAviso(onAviso)));
  const quitar = useMutation(trpc.correcciones.quitar.mutationOptions(conAviso(onAviso)));
  const responder = useMutation(
    trpc.correcciones.responder.mutationOptions(
      conAviso(onAviso, () => {
        setRespuesta("");
        setRespondiendo(false);
      }),
    ),
  );
  useEffect(() => {
    if (activa) fila.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activa]);
  const ocupado = resolver.isPending || quitar.isPending || responder.isPending;

  return (
    <li ref={fila} className={`px-4 py-3 ${activa ? "bg-marino-claro/50" : ""}`}>
      <button type="button" onClick={onElegir} className="block w-full text-left">
        <div className="flex items-center gap-2">
          <span
            className={`grid h-5 min-w-5 place-items-center rounded px-1 text-[11px] font-bold text-white ${COLOR_TIPO[x.tipo]}`}
          >
            {x.numero}
          </span>
          <span className="text-[12px] text-texto-3">
            {x.autor.nombre} · {formatearFechaHora(x.creadaEn)}
          </span>
          {x.estado === "resuelta" ? (
            <Pildora tono="bg-verde-fondo text-verde" className="ml-auto">
              ✓ Resuelta
            </Pildora>
          ) : (
            <Pildora tono="bg-ambar-fondo text-ambar" className="ml-auto">
              Abierta
            </Pildora>
          )}
        </div>
        {x.ancla ? (
          <blockquote className="mt-1.5 text-[12.5px] italic text-texto-2">
            «{x.ancla.citado}»
            {(x.tramos ?? []).length > 0 && (
              <span className="not-italic text-texto-3">
                {" "}
                … y {(x.tramos ?? []).length} tramo{(x.tramos ?? []).length > 1 ? "s" : ""} más
              </span>
            )}
          </blockquote>
        ) : (
          <div className="mt-1.5 text-[12px] font-semibold text-texto-3">Comentario general</div>
        )}
        {x.ancla && !x.lugar && (
          <div className="mt-1 text-[11.5px] text-ambar">
            Esa frase cambió: ya no está en el texto.
          </div>
        )}
        <p className="mt-1 text-[13px] whitespace-pre-line">
          {x.tipo !== "corregir" && <strong>{NOMBRE_TIPO_CORRECCION[x.tipo]}: </strong>}
          {x.comentario}
        </p>
      </button>
      {x.resuelta && (
        <div className="mt-1 text-[11.5px] text-verde">
          Resuelta por {x.resuelta.por} · {formatearFechaHora(x.resuelta.en)}
        </div>
      )}
      {x.respuestas.length > 0 && (
        <ul className="mt-2 space-y-1.5 border-l-2 border-borde pl-3">
          {x.respuestas.map((r) => (
            <li key={r.id} className="text-[12.5px]">
              <span className="font-semibold">{r.autor}</span>{" "}
              <span className="text-texto-3">· {formatearFechaHora(r.creadaEn)}</span>
              <div className="whitespace-pre-line">{r.texto}</div>
            </li>
          ))}
        </ul>
      )}
      {respondiendo ? (
        <form
          className="mt-2 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            responder.mutate({ correccionId: x.id, texto: respuesta });
          }}
        >
          <textarea
            value={respuesta}
            onChange={(e) => setRespuesta(e.target.value)}
            rows={2}
            aria-label="Respuesta"
            className={CLASE_ENTRADA}
          />
          <div className="flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setRespondiendo(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" disabled={ocupado || !respuesta.trim()}>
              Responder
            </Boton>
          </div>
        </form>
      ) : (
        <div className="mt-2 flex flex-wrap gap-3 text-[12px] font-semibold text-marino">
          {x.puedeResolver && (
            <button
              type="button"
              disabled={ocupado}
              onClick={() =>
                resolver.mutate({ correccionId: x.id, resuelta: x.estado === "abierta" })
              }
              className="hover:underline disabled:opacity-60"
            >
              {x.estado === "abierta" ? "Marcar resuelta" : "Volver a abrir"}
            </button>
          )}
          <button type="button" onClick={() => setRespondiendo(true)} className="hover:underline">
            Responder
          </button>
          {x.puedeQuitar && (
            <button
              type="button"
              disabled={ocupado}
              onClick={() => quitar.mutate({ correccionId: x.id })}
              className="text-rojo hover:underline disabled:opacity-60"
            >
              Quitar
            </button>
          )}
        </div>
      )}
    </li>
  );
}
